import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";

const KEY = "gvb_welcomed";
const LINE = "#FFFFFF"; // thick white painted lines on brick

// Brick intro screen: the line illustration is drawn stroke-by-stroke (true SVG
// path tracing), fills in, holds, then the whole screen fades into the site.
// Shows once per browser session.
export const WelcomeScreen = () => {
  const [show, setShow] = useState(() => {
    if (typeof window === "undefined") return false;
    return !window.sessionStorage.getItem(KEY);
  });
  const holderRef = useRef(null);

  // Auto-dismiss timer
  useEffect(() => {
    if (!show) return;
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    document.body.style.overflow = "hidden";
    const t = setTimeout(() => setShow(false), reduce ? 900 : 4200);
    return () => {
      clearTimeout(t);
      document.body.style.overflow = "";
    };
  }, [show]);

  // Load + trace the SVG
  useEffect(() => {
    if (!show) return;
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    let cancelled = false;

    fetch("/welcome-illustration.svg")
      .then((r) => r.text())
      .then((svg) => {
        if (cancelled || !holderRef.current) return;
        const holder = holderRef.current;
        holder.style.opacity = "0"; // hide the raw (fully-filled) svg until we blank it
        holder.innerHTML = svg;
        const paths = Array.from(holder.querySelectorAll("path"));

        paths.forEach((p, i) => {
          const len = p.getTotalLength ? p.getTotalLength() : 1000;
          p.style.fill = "none";
          p.style.stroke = LINE;
          p.style.strokeWidth = "4";
          p.style.strokeLinecap = "round";
          p.style.strokeLinejoin = "round";
          if (reduce) {
            p.style.fill = LINE;
            p.style.stroke = "none";
            return;
          }
          p.style.strokeDasharray = String(len);
          p.style.strokeDashoffset = String(len);
          p.style.transition = "stroke-dashoffset 0.4s ease, fill 0.25s ease";
        });

        holder.style.opacity = "1"; // reveal the now-blank canvas (no filled flash)

        if (reduce) return;

        // Paint each piece on quickly, then fill it solid — one after another
        requestAnimationFrame(() => {
          paths.forEach((p, i) => {
            const at = i * 40;
            setTimeout(() => {
              if (!cancelled) p.style.strokeDashoffset = "0";
            }, at);
            setTimeout(() => {
              if (!cancelled) p.style.fill = LINE;
            }, at + 360);
          });
        });
      })
      .catch(() => setShow(false));

    return () => {
      cancelled = true;
    };
  }, [show]);

  const onDone = () => {
    window.sessionStorage.setItem(KEY, "1");
    document.body.style.overflow = "";
  };

  return (
    <AnimatePresence onExitComplete={onDone}>
      {show && (
        <motion.div
          key="welcome"
          data-testid="welcome-screen"
          className="fixed inset-0 z-[100] flex cursor-pointer items-center justify-center bg-terracotta px-6"
          initial={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 1.6, ease: [0.4, 0, 0.2, 1] }}
          onClick={() => setShow(false)}
        >
          <div
            ref={holderRef}
            data-testid="welcome-illustration"
            className="welcome-svg w-full max-w-4xl"
          />
        </motion.div>
      )}
    </AnimatePresence>
  );
};
